import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  ErrorBanner,
  SubmitButton,
  Textarea,
} from '@saas-kit/ui';

import { LogoUploader } from './LogoUploader';
import { useBranding } from './useBranding';

/** Tenant-wide queue branding: logo plus ticket header/footer copy. */
export function BrandingTab(): JSX.Element {
  const { t } = useTranslation('qms');
  const branding = useBranding();

  const [headerText, setHeaderText] = useState('');
  const [footerText, setFooterText] = useState('');
  const [logoId, setLogoId] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!branding.branding) return;
    setHeaderText(branding.branding.header_text ?? '');
    setFooterText(branding.branding.footer_text ?? '');
    setLogoId(branding.branding.logo_file_id);
    setLogoUrl(branding.branding.logo_url);
  }, [branding.branding]);

  const pickLogo = async (file: File): Promise<void> => {
    const logo = await branding.uploadLogo(file);
    setLogoId(logo.file_id);
    setLogoUrl(logo.logo_url);
  };

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    await branding.save({
      logo_file_id: logoId,
      header_text: headerText.trim() || null,
      footer_text: footerText.trim() || null,
    });
  };

  if (branding.isError) {
    return <ErrorBanner>{t('errors.branding_denied')}</ErrorBanner>;
  }

  return (
    <Card>
      <CardContent className="p-5">
        <form onSubmit={(e) => void submit(e)} className="space-y-5">
          <div>
            <p className="text-sm font-medium text-foreground">
              {t('branding.title')}
            </p>
            <p className="text-sm text-muted-foreground">
              {t('branding.subtitle')}
            </p>
          </div>

          <LogoUploader
            currentUrl={logoUrl}
            uploading={branding.uploadingLogo}
            onPick={(file) => void pickLogo(file)}
            onClear={() => {
              setLogoId(null);
              setLogoUrl(null);
            }}
          />

          <label className="block space-y-1 text-sm">
            <span className="text-foreground">
              {t('branding.header_text')}
            </span>
            <Textarea
              value={headerText}
              onChange={(e) => setHeaderText(e.target.value)}
              rows={2}
              maxLength={2000}
              placeholder={t('branding.header_placeholder')}
              data-testid="branding-header-text"
            />
          </label>

          <label className="block space-y-1 text-sm">
            <span className="text-foreground">
              {t('branding.footer_text')}
            </span>
            <Textarea
              value={footerText}
              onChange={(e) => setFooterText(e.target.value)}
              rows={4}
              maxLength={4000}
              placeholder={t('branding.footer_placeholder')}
              data-testid="branding-footer-text"
            />
          </label>

          <SubmitButton
            loading={branding.saving}
            label={t('actions.save')}
            loadingLabel={t('actions.save')}
          />
        </form>
      </CardContent>
    </Card>
  );
}
